import {
  Edit,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  useForm,
  useSelect,
} from "@pankod/refine-antd";
import { IResourceComponentsProps, useShow } from "@pankod/refine-core";
import { useEffect, useState } from "react";
import "react-mde/lib/styles/css/react-mde-all.css";
import { ILabel } from "types/label.interface";
import "./styles.scss";

export const OptionReleasePricingEdit: React.FC<
  IResourceComponentsProps
> = () => {
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(500);
  const [minTracks, setMinTracks] = useState<number>(0);
  const [maxTracks, setMaxTracks] = useState<number>(500);

  const { queryResult } = useShow<ILabel>();
  const { data, isLoading } = queryResult;
  const record: any = data?.data;
  const { formProps, saveButtonProps, formLoading } = useForm<ILabel>();

  useEffect(() => {
    setMinPrice(record?.data?.minPrice);
    setMaxPrice(record?.data?.maxPrice);
    setMinTracks(record?.data?.minTracks);
    setMaxTracks(record?.data?.maxTracks);
  }, [
    record?.data?.minPrice,
    record?.data?.maxPrice,
    record?.data?.minTracks,
    record?.data?.maxTracks,
  ]);

  const { selectProps: LabelSelectProps } = useSelect<ILabel>({
    resource: "labels",
    fetchSize: 100,
    optionLabel: "name",
    optionValue: "_id",
  });

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      isLoading={isLoading || formLoading}
    >
      <div style={{ display: "flex", height: "100%" }}>
        <Form {...formProps} layout="vertical" style={{ width: "100%" }}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={record?.data?.name}
          >
            <Input defaultValue={record?.data?.name} />
          </Form.Item>
          <div className="edit-title">
            <Form.Item
              className="edit-item w48"
              label="Min Tracks"
              name="minTracks"
              rules={[
                {
                  required: true,
                  message: "Min Tracks is required",
                },
                {
                  min: 1,
                  type: "number",
                  message: "Min Tracks must be more than 0",
                },
                {
                  validator: (_, value) => {
                    if (value && maxTracks && value > maxTracks) {
                      return Promise.reject(
                        new Error(
                          "Min Tracks must be less than or equal to Max Tracks"
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              initialValue={record?.data?.minTracks}
            >
              <InputNumber
                // disabled
                defaultValue={record?.data?.minTracks}
                type="number"
                onChange={(value: number) => setMinTracks(value)}
              />
            </Form.Item>
            <Form.Item
              className="edit-item w48"
              label="Max Tracks"
              name="maxTracks"
              rules={[
                {
                  required: true,
                  message: "Max Tracks is required",
                },
                {
                  min: 1,
                  type: "number",
                  message: "Max Tracks must be more than 0",
                },
                {
                  validator: (_, value) => {
                    if (value && minTracks && value < minTracks) {
                      return Promise.reject(
                        new Error(
                          "Max Tracks must be greater than or equal to Min Tracks"
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              initialValue={record?.data?.maxTracks}
            >
              <InputNumber
                // disabled
                defaultValue={record?.data?.maxTracks}
                type="number"
                onChange={(value: number) => setMaxTracks(value)}
              />
            </Form.Item>
          </div>
          <div>
            <div className="edit-title">
              <Form.Item
                className="edit-item w48"
                label="Min Price"
                name="minPrice"
                rules={[
                  {
                    required: true,
                    message: "Min Price is required",
                  },
                  {
                    min: 1,
                    type: "number",
                    message: "Min Price must be 1 or more",
                  },
                  {
                    validator: (_, value) => {
                      if (value && maxPrice && value > maxPrice) {
                        return Promise.reject(
                          new Error(
                            "Min Price must be less than or equal to Max Price"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                initialValue={record?.data?.minPrice}
              >
                <InputNumber
                  defaultValue={record?.data?.minPrice}
                  type="number"
                  onChange={(value: number) => setMinPrice(value)}
                />
              </Form.Item>
              <Form.Item
                className="edit-item w48"
                label="Max Price"
                name="maxPrice"
                rules={[
                  {
                    required: true,
                    message: "Max Price is required",
                  },
                  {
                    min: 1,
                    type: "number",
                    message: "Max Price must be 1 or more",
                  },
                  {
                    validator: (_, value) => {
                      if (value && minPrice && value < minPrice) {
                        return Promise.reject(
                          new Error(
                            "Max Price must be greater than or equal to Min Price"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                initialValue={record?.data?.maxPrice}
              >
                <InputNumber
                  defaultValue={record?.data?.maxPrice}
                  type="number"
                  onChange={(value: number) => setMaxPrice(value)}
                />
              </Form.Item>
            </div>
          </div>

          <Form.Item
            label="Labels"
            name="labels"
            rules={[
              {
                type: "array",
                min: 1,
              },
              {
                required: false,
                message: "Labels is required",
              },
            ]}
            initialValue={record?.data?.labels?.map((label: any) => label?._id)}
          >
            <Select mode="multiple" {...LabelSelectProps} />
          </Form.Item>
          <Form.Item
            className="edit-item w48"
            label="Is Active"
            name="isActive"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={record?.data?.isActive}
          >
            <Switch defaultChecked={record?.data?.isActive} />
          </Form.Item>
          <Form.Item
            className="edit-item w48"
            label="Is Mixinit"
            name="isMixinit"
            rules={[
              {
                required: false,
              },
            ]}
            initialValue={record?.data?.isMixinit}
          >
            <Switch defaultChecked={record?.data?.isMixinit} />
          </Form.Item>
        </Form>
      </div>
    </Edit>
  );
};
